import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { type ReactNode, useId } from "react";
import { useIsMobile } from "@/hooks/useMediaQuery";

export interface GfgModalProps {
  open: boolean;
  onClose: () => void;
  onPrimary: () => void;
  onSecondary?: () => void;
  icon: ReactNode;
  title: string;
  description: ReactNode;
  primaryLabel: string;
  /** Omit to render a single-button modal (e.g. an informational dismiss). */
  secondaryLabel?: string;
  primaryFirst?: boolean;
  primaryIcon?: ReactNode;
  /**
   * "primary" (default, rodeo black) or "danger" (wine) — the primary button's fill. Callers that
   * omit this keep today's rodeo-black appearance unchanged; opt into "danger" only for a
   * confirm that is destructive (e.g. cancelling a send).
   */
  primaryVariant?: "primary" | "danger";
  /**
   * Runs on backdrop click and Escape ONLY — the buttons never reach it. Pass the secondary
   * handler to make dismissing equivalent to declining, which is what a paywall wants: the
   * gated choice is undone however the modal was left.
   *
   * Opt-in per caller, deliberately. Wiring dismissal to onSecondary for every modal would
   * make a backdrop click on "unsaved changes" run its Discard-and-leave secondary, so
   * brushing past the dialog would throw away the user's work.
   */
  onDismiss?: () => void;
  /**
   * Backdrop click and Escape close the dialog. Pass false for a modal that gates an action
   * the caller must answer — dismissing it then runs onPrimary, onSecondary and onDismiss
   * alike never, so state a paywall reverts in one of them would be silently bypassed.
   */
  dismissible?: boolean;
}

/**
 * Fill + hover colour per `primaryVariant`. "danger"'s hover reuses the same darkened-wine hex
 * GfgButton's `danger` variant already hovers to (`hover:bg-[#8f3c36]` in GfgButton.tsx) so the
 * two primitives darken identically; "primary"'s hover is the pre-existing hard-coded `#1a1a1a`,
 * unchanged.
 */
const PRIMARY_VARIANT_FILL: Record<"primary" | "danger", { bg: string; hoverBg: string }> = {
  primary: { bg: "var(--color-rodeo-black)", hoverBg: "#1a1a1a" },
  danger: { bg: "var(--color-good-wine)", hoverBg: "#8f3c36" },
};

export default function GfgModal({
  open,
  onClose,
  onPrimary,
  onSecondary,
  icon,
  title,
  description,
  primaryLabel,
  secondaryLabel,
  primaryFirst,
  primaryIcon,
  onDismiss,
  dismissible = true,
  primaryVariant = "primary",
}: GfgModalProps) {
  const isMobile = useIsMobile();
  // Without this the dialog is announced as an unnamed "dialog"; the title is already on screen,
  // so pointing at it names every caller at once rather than each passing a label of its own.
  const titleId = useId();

  const primaryFill = PRIMARY_VARIANT_FILL[primaryVariant];

  const primaryBtn = (
    <Button
      onClick={() => {
        onClose();
        onPrimary();
      }}
      startIcon={primaryIcon}
      disableElevation
      sx={{
        fontFamily: "var(--font-lato)",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "1.98px",
        textTransform: "uppercase",
        bgcolor: primaryFill.bg,
        color: "white",
        borderRadius: "var(--radius-btn)",
        px: "20px",
        py: "10px",
        height: "34px",
        flexShrink: 0,
        "& .MuiButton-startIcon svg": { fontSize: "14px !important" },
        "&:hover": { bgcolor: primaryFill.hoverBg },
      }}
    >
      {primaryLabel}
    </Button>
  );

  const secondaryBtn = secondaryLabel ? (
    <Button
      onClick={() => {
        onClose();
        onSecondary?.();
      }}
      disableRipple
      sx={{
        fontFamily: "var(--font-lato)",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "1.98px",
        textTransform: "uppercase",
        color: "var(--color-dark-dust)",
        p: 0,
        minWidth: "unset",
        bgcolor: "transparent",
        flexShrink: 0,
        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
      }}
    >
      {secondaryLabel}
    </Button>
  ) : null;

  return (
    <Dialog
      open={open}
      aria-labelledby={titleId}
      // Withholding onClose is what blocks the backdrop; disableEscapeKeyDown covers the key.
      // The buttons call the onClose prop directly, so they still close either way — which is
      // also why onDismiss belongs here and only here: this handler IS the dismissal path.
      onClose={
        dismissible
          ? () => {
              onClose();
              onDismiss?.();
            }
          : undefined
      }
      disableEscapeKeyDown={!dismissible}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            border: "1px solid var(--color-smoke)",
            p: "20px",
            m: 2,
            width: 577,
            maxWidth: "calc(100vw - 32px)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "44px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              bgcolor: "var(--color-clear-dust)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            {icon}
          </Box>

          <Typography
            id={titleId}
            sx={{
              fontFamily: "var(--font-crimson)",
              fontSize: "30px",
              fontWeight: 400,
              lineHeight: "normal",
              color: "var(--color-rodeo-black)",
              textAlign: "center",
            }}
          >
            {title}
          </Typography>

          {typeof description === "string" ? (
            <Typography
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: "16px",
                color: "var(--color-graphite)",
                textAlign: "center",
                lineHeight: "normal",
              }}
            >
              {description}
            </Typography>
          ) : (
            description
          )}
        </Box>

        {/* On mobile the 44px gap + two long labels overflow the 343px paper
            (calc(100vw - 32px) at 375): wrap and tighten so both buttons fit. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "16px" : "44px",
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "center" : "flex-start",
          }}
        >
          {primaryFirst ? (
            <>
              {primaryBtn}
              {secondaryBtn}
            </>
          ) : (
            <>
              {secondaryBtn}
              {primaryBtn}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
